// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { formatISODate } from "@clipboard-health/date-time";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api/constants";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { isDeviceLocationAlwaysAllowPermissionGranted } from "@src/appV2/Location/deviceLocation/permissions";
import { isOsWithoutAlwaysAllowOption } from "@src/appV2/Location/utils";
import {
  PLACEMENT_CANDIDATE_BAN_ACTION,
  type PlacementCandidate,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { type QueryObserverResult } from "@tanstack/react-query";
import { differenceInHours, isAfter, isBefore, isEqual, parseISO, startOfDay } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { useEffect } from "react";

import { useCreateInterview } from "../../../../api/useCreateInterview";
import { PLACEMENT_TYPE } from "../../../../api/useCreatePlacementApplication";
import { type GetAvailabilityResponse } from "../../../../api/useGetInterviewAvailability";
import { type PlacementDetail } from "../../../../api/useGetPlacement";
import { type PlacementResponse } from "../../../../types/fetchPlacements.schema";
import { type InterviewTime } from "../../../InterviewTimeSelector";
import { PLACEMENT_ACTION_TYPE, type PlacementActionType } from "../../utils/getPrimaryAction";
import { type PlacementDerivedData } from "../usePlacementPrimaryAction";
import { type usePlacementState } from "../usePlacementState";
import { type ApplicationOperationsReturn } from "./useApplicationOperations";

interface UseInterviewOperationsParams {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  modals: ReturnType<typeof usePlacementState>["modals"];
  interviewState: ReturnType<typeof usePlacementState>["interviewState"];
  locationState: ReturnType<typeof usePlacementState>["locationState"];
  derivedData: PlacementDerivedData;
  applicationOperations: ApplicationOperationsReturn;
  refetchPlacement: () => Promise<QueryObserverResult<PlacementResponse>>;
  refetchInterviewAvailability: () => Promise<QueryObserverResult<GetAvailabilityResponse>>;
  refetchPlacementCandidate: () => Promise<QueryObserverResult<PlacementCandidate>>;
}

/**
 * Finds a hiring event that matches the given interview time.
 */
function findHiringEventForTime(
  zonedTimeStart: Date,
  hiringEvents: PlacementDetail["hiringEvents"]
) {
  return hiringEvents?.find((hiringEvent) => {
    const schedule = hiringEvent.attributes.fixedSchedule;
    const startDateIso = schedule?.startDate;
    const endDateIso = schedule?.endDate;
    if (!startDateIso || !endDateIso) {
      return false;
    }

    const startDate = parseISO(startDateIso);
    const endDate = parseISO(endDateIso);
    return (
      (isAfter(zonedTimeStart, startDate) || isEqual(zonedTimeStart, startDate)) &&
      isBefore(zonedTimeStart, endDate)
    );
  });
}

/**
 * Handles all interview-related operations (book, reschedule, location permissions).
 */
export function useInterviewOperations(params: UseInterviewOperationsParams) {
  const {
    placementDetails,
    modals,
    interviewState,
    locationState,
    derivedData,
    applicationOperations,
    refetchPlacement,
    refetchInterviewAvailability,
    refetchPlacementCandidate,
  } = params;

  const errorMessages = useCbhFlag(CbhFeatureFlag.HCP_APP_ERROR_MESSAGES, { defaultValue: {} });
  const { showErrorToast } = useToast();
  const { mutateAsync: createInterview, isLoading: isCreatingInterview } = useCreateInterview();

  const {
    workerTimezone,
    hasApplied,
    isSignOnBonusPaid,
    incentiveProgram,
    isLateCancellation,
    isReschedulingPastInterview,
    eventContext,
    isInterviewLocationPermissionsCheckEnabled,
    isInterviewLocationPermissionsRequired,
    hiringReliability,
  } = derivedData;
  const {
    selectedInterviewTime,
    setSelectedInterviewTime,
    bookInterviewModalType,
    setBookInterviewModalType,
    isRescheduleForToday,
    setIsRescheduleForToday,
  } = interviewState;
  const { applicationPreferences } = applicationOperations;
  const {
    isOsWithoutAlwaysAllow,
    setIsOsWithoutAlwaysAllow,
    shouldShowLocationPermissionDialog,
    setShouldShowLocationPermissionDialog,
  } = locationState;

  // Show location permission dialog after interview booked dialog closes
  useEffect(() => {
    if (shouldShowLocationPermissionDialog && !modals.interviewBooked.modalIsOpen) {
      modals.locationPermission.openModal();
      setShouldShowLocationPermissionDialog(false);
    }
  }, [
    shouldShowLocationPermissionDialog,
    setShouldShowLocationPermissionDialog,
    modals.interviewBooked.modalIsOpen,
    modals.locationPermission,
  ]);

  async function checkLocationPermissionsAfterInterviewBooking() {
    if (!isCapacitorPlatform() || !isInterviewLocationPermissionsCheckEnabled) {
      return;
    }

    try {
      const hasAlwaysPermission = await isDeviceLocationAlwaysAllowPermissionGranted();
      const osWithoutAlwaysAllow = await isOsWithoutAlwaysAllowOption();
      setIsOsWithoutAlwaysAllow(osWithoutAlwaysAllow);

      if (!hasAlwaysPermission) {
        setShouldShowLocationPermissionDialog(true);
        logEvent(APP_V2_APP_EVENTS.INTERVIEW_LOCATION_PERMISSION_BOTTOM_SHEET_SHOWN_AFTER_BOOKING, {
          ...eventContext,
        });
      }
    } catch {
      // If we can't check the permission status, we'll assume it's not granted and show the dialog
      setShouldShowLocationPermissionDialog(true);
      logEvent(APP_V2_APP_EVENTS.INTERVIEW_LOCATION_PERMISSION_BOTTOM_SHEET_SHOWN_AFTER_BOOKING, {
        ...eventContext,
      });
    }
  }

  async function checkLocationPermissionsBeforeInterviewBooking(): Promise<boolean> {
    if (!isCapacitorPlatform() || !isInterviewLocationPermissionsRequired) {
      return true;
    }

    try {
      const hasAlwaysPermission = await isDeviceLocationAlwaysAllowPermissionGranted();
      const osWithoutAlwaysAllow = await isOsWithoutAlwaysAllowOption();
      setIsOsWithoutAlwaysAllow(osWithoutAlwaysAllow);

      if (!hasAlwaysPermission) {
        modals.interviewLocationPermission.openModal();
        logEvent(
          APP_V2_APP_EVENTS.INTERVIEW_LOCATION_PERMISSION_BOTTOM_SHEET_SHOWN_BEFORE_BOOKING,
          {
            ...eventContext,
            placementActionType: bookInterviewModalType,
          }
        );
        return false;
      }

      return true;
    } catch {
      // If we can't check the permission status, we'll assume it's not granted and show the dialog
      modals.interviewLocationPermission.openModal();
      logEvent(APP_V2_APP_EVENTS.INTERVIEW_LOCATION_PERMISSION_BOTTOM_SHEET_SHOWN_BEFORE_BOOKING, {
        ...eventContext,
        placementActionType: bookInterviewModalType,
      });
      return false;
    }
  }

  function handleInterviewError(error: unknown, defaultMessage: string): void {
    if (!isCbhApiAxiosError(error)) {
      showErrorToast(defaultMessage);
      return;
    }

    const errorResponse = error.response.data.errors.find((error) => error.code);
    if (errorResponse && errorMessages[errorResponse.code]) {
      showErrorToast(errorMessages[errorResponse.code]);
      return;
    }

    // Fallback to showing API error details
    const errorDetails = error.response.data.errors
      .map((error) => error.detail)
      .filter(Boolean)
      .join(", ");
    if (errorDetails) {
      showErrorToast(
        <Text variant="body2" color="inherit">
          {errorDetails}
        </Text>
      );
      return;
    }

    showErrorToast(defaultMessage);
  }

  async function handleBookInterview(time: { start: Date; end: Date }): Promise<boolean> {
    try {
      const zonedTimeStart = zonedTimeToUtc(time.start, workerTimezone);
      const zonedTimeEnd = zonedTimeToUtc(time.end, workerTimezone);

      // get the hiring event that matches the time.start
      const hiringEvent = findHiringEventForTime(zonedTimeStart, placementDetails.hiringEvents);

      if (hasApplied) {
        const placementApplication = placementDetails?.applications?.at(-1);

        if (!placementApplication) {
          throw new Error("No placement application found");
        }

        const createInterviewPayload = {
          relatedResource: {
            type: API_TYPES.placementApplication,
            id: placementApplication.id,
          },
          ...(isDefined(hiringEvent) && {
            hiringEvent: {
              data: {
                type: API_TYPES.hiringEvent,
                id: hiringEvent.id,
              },
            },
          }),
          start: zonedTimeStart,
          end: zonedTimeEnd,
        };

        await createInterview(createInterviewPayload);
        return true;
      }

      const applyPayload = {
        placementType: PLACEMENT_TYPE.verified,
        scheduleInterview: {
          start: zonedTimeStart.toISOString(),
          end: zonedTimeEnd.toISOString(),
        },
        hiringEventId: hiringEvent?.id,
        ...(isDefined(incentiveProgram) && !isSignOnBonusPaid ? { incentiveProgram } : {}),
        ...(isDefined(applicationPreferences) && {
          workerApplicationPreferenceDetails: applicationPreferences,
        }),
      };

      await applicationOperations.handleApply(applyPayload);
      return true;
    } catch (error) {
      handleInterviewError(error, "Failed to book interview.");
      return false;
    }
  }

  async function handleRescheduleInterview(time: { start: Date; end: Date }): Promise<boolean> {
    try {
      const zonedTimeStart = zonedTimeToUtc(time.start, workerTimezone);
      const zonedTimeEnd = zonedTimeToUtc(time.end, workerTimezone);

      // Prioritize in-person interviews over phone screen interviews
      const bookedInterviews = placementDetails.interviews.filter(
        (interview) => interview.attributes.status === InterviewStatus.BOOKED
      );
      const phoneScreenInterview = bookedInterviews.find(
        (interview) => interview.attributes.type === "phone-screen"
      );
      const inPersonInterview = bookedInterviews.find(
        (interview) => interview.attributes.type === "in-person-interview"
      );
      const rescheduledInterview = inPersonInterview ?? phoneScreenInterview ?? bookedInterviews[0];

      if (!rescheduledInterview) {
        throw new Error(
          "Could not find an interview to reschedule based on the status and start date"
        );
      }

      // get the hiring event that matches the time.start
      const hiringEvent = findHiringEventForTime(zonedTimeStart, placementDetails.hiringEvents);

      await createInterview({
        relatedResource: {
          type: API_TYPES.interview,
          id: rescheduledInterview.id,
        },
        start: zonedTimeStart,
        end: zonedTimeEnd,
        ...(isDefined(rescheduledInterview.attributes.type) && {
          interviewType: rescheduledInterview.attributes.type,
        }),
        ...(isDefined(hiringEvent) && {
          hiringEvent: {
            data: {
              type: API_TYPES.hiringEvent,
              id: hiringEvent.id,
            },
          },
        }),
      });
      return true;
    } catch (error) {
      handleInterviewError(error, "Failed to reschedule interview.");
      return false;
    }
  }

  function shouldShowLateRescheduleModal(time: InterviewTime): boolean {
    // Prioritize in-person interviews over phone screen interviews
    const bookedInterviews = placementDetails.interviews.filter(
      (interview) => interview.attributes.status === InterviewStatus.BOOKED
    );
    const phoneScreenInterview = bookedInterviews.find(
      (interview) => interview.attributes.type === "phone-screen"
    );
    const inPersonInterview = bookedInterviews.find(
      (interview) => interview.attributes.type === "in-person-interview"
    );
    const currentInterview = inPersonInterview ?? phoneScreenInterview ?? bookedInterviews[0];

    const candidateBanForSameInterview = placementDetails.candidateBans?.find(
      (candidateBan) =>
        candidateBan.relationships.interview?.data.id === currentInterview?.id &&
        candidateBan.attributes.action === PLACEMENT_CANDIDATE_BAN_ACTION.INTERVIEW_NCNS
    );

    return (
      bookInterviewModalType === PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW &&
      (isLateCancellation || isReschedulingPastInterview) &&
      !isDefined(candidateBanForSameInterview) &&
      isRescheduleToFutureDay(time) &&
      !hiringReliability.simplifiedBanEnabled
    );
  }

  async function handleBookOrRescheduleInterview(time: { start: Date; end: Date }) {
    const success = await (bookInterviewModalType === PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW
      ? handleBookInterview(time)
      : handleRescheduleInterview(time));
    await Promise.all([refetchPlacement(), refetchInterviewAvailability()]);

    await refetchPlacementCandidate();

    if (success) {
      modals.interviewBooked.openModal();
      await checkLocationPermissionsAfterInterviewBooking();
    }

    return success;
  }

  function isRescheduleToFutureDay(newTime: { start: Date; end: Date }): boolean {
    // Prioritize in-person interviews over phone screen interviews
    const bookedInterviews = placementDetails.interviews.filter(
      (interview) => interview.attributes.status === InterviewStatus.BOOKED
    );
    const phoneScreenInterview = bookedInterviews.find(
      (interview) => interview.attributes.type === "phone-screen"
    );
    const inPersonInterview = bookedInterviews.find(
      (interview) => interview.attributes.type === "in-person-interview"
    );
    const currentInterview = inPersonInterview ?? phoneScreenInterview ?? bookedInterviews[0];

    if (!currentInterview) {
      return false;
    }

    const currentDate = startOfDay(parseISO(currentInterview.attributes.start));
    const newDate = startOfDay(newTime.start);

    // add 8 hour difference check in case of timezone weirdness
    return isAfter(newDate, currentDate) && differenceInHours(newDate, currentDate) > 8;
  }

  async function openBookInterviewModal(
    type: PlacementActionType,
    interviewAvailability: Array<{ start: Date }>,
    isRescheduleToToday = false
  ) {
    // Check location permissions before allowing interview booking
    const hasLocationPermissions = await checkLocationPermissionsBeforeInterviewBooking();
    if (!hasLocationPermissions) {
      return;
    }

    // Check if this is a phone screen and show phone confirmation first
    const isPhoneScreen =
      placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType ===
      "phone-screen";
    if (isPhoneScreen && type === PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW) {
      modals.phoneConfirmation.openModal();
      // Store the type and reschedule state so we can continue after phone confirmation
      setBookInterviewModalType(type);
      setIsRescheduleForToday(isRescheduleToToday);
      return;
    }

    setBookInterviewModalType(type);
    setIsRescheduleForToday(isRescheduleToToday);
    modals.bookInterview.openModal();
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_BOOKING_BOTTOMSHEET_OPENED, {
      ...eventContext,
      placementActionType: type,
      totalTimeSlots: interviewAvailability.length,
      totalAvailableDates: new Set(interviewAvailability.map((slot) => formatISODate(slot.start)))
        .size,
    });
  }

  function continueAfterPhoneConfirmation(interviewAvailability: Array<{ start: Date }>) {
    // Open the interview booking modal after phone confirmation
    modals.bookInterview.openModal();
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_BOOKING_BOTTOMSHEET_OPENED, {
      ...eventContext,
      placementActionType: bookInterviewModalType,
      totalTimeSlots: interviewAvailability.length,
      totalAvailableDates: new Set(interviewAvailability.map((slot) => formatISODate(slot.start)))
        .size,
    });
  }

  return {
    handleBookInterview,
    handleRescheduleInterview,
    handleBookOrRescheduleInterview,
    openBookInterviewModal,
    continueAfterPhoneConfirmation,
    shouldShowLateRescheduleModal,
    checkLocationPermissionsAfterInterviewBooking,
    isCreatingInterview,
    selectedInterviewTime,
    setSelectedInterviewTime,
    bookInterviewModalType,
    isOsWithoutAlwaysAllow,
    isRescheduleToFutureDay,
    isRescheduleForToday,
  };
}

export type InterviewOperationsReturn = ReturnType<typeof useInterviewOperations>;
